/**
 * 電子發票設定 schema — REQ-20260602001,REQ-20260915003 改串 ezPay。
 *
 * 串接方式:ezPay 電子發票加值中心 API(`/Api/invoice_issue`)。字軌配號、上傳財政部
 * 都由 ezPay 處理,系統只需要商店代號 + HashKey / HashIV。
 * DB 僅存一組(key='einvoice');UI / API 皆以這份為 contract。
 */
import { z } from 'zod';

/** 開立環境:ezPay 測試平台(cinv)vs 正式平台(inv)。預設 test,避免誤開正式發票。 */
export const EinvoiceEnv = z.enum(['test', 'production']);
export type EinvoiceEnv = z.infer<typeof EinvoiceEnv>;

/**
 * 使用者輸入(PUT /api/v1/settings/einvoice)。
 * hashKey / hashIV 留空代表「保留原值」(同 SMTP password 的處理)。
 */
export const EinvoiceSettingInputSchema = z.object({
  /** 是否啟用電子發票開立(關閉時系統不對外開票) */
  enabled: z.boolean().default(false),
  /** 開立環境;正式上線前一律維持 test。注意 ezPay 測試與正式的商店代號 / 金鑰不共用 */
  environment: EinvoiceEnv.default('test'),
  /** 賣方統一編號(8 碼,印在證明聯) */
  sellerTaxId: z
    .string()
    .trim()
    .regex(/^\d{8}$/u, '賣方統一編號須為 8 位數字')
    .or(z.literal('')),
  /** 賣方營業人名稱(印在證明聯) */
  sellerName: z.string().trim().max(60).default(''),
  /** 賣方地址(REQ-20260817003:請款 / 對帳文件的「我方公司」抬頭) */
  address: z.string().trim().max(200).default(''),
  /** 賣方電話(同上) */
  phone: z.string().trim().max(50).default(''),
  /** 賣方傳真(同上) */
  fax: z.string().trim().max(50).default(''),
  /** 預設稅率(應稅,通常 0.05) */
  defaultTaxRate: z.coerce.number().min(0).max(1).default(0.05),
  /** ezPay 商店代號(MerchantID) */
  merchantId: z
    .string()
    .trim()
    .max(20)
    .regex(/^[A-Za-z0-9]*$/u, '商店代號只能是英文或數字')
    .default(''),
  /** ezPay HashKey(AES-256 金鑰,32 碼;機敏,留空保留原值) */
  hashKey: z
    .string()
    .trim()
    .refine((v) => v === '' || v.length === 32, 'HashKey 須為 32 碼')
    .default(''),
  /** ezPay HashIV(AES IV,16 碼;機敏,留空保留原值) */
  hashIV: z
    .string()
    .trim()
    .refine((v) => v === '' || v.length === 16, 'HashIV 須為 16 碼')
    .default(''),
});
export type EinvoiceSettingInput = z.infer<typeof EinvoiceSettingInputSchema>;

/** API 回傳(不含明文 HashKey / HashIV) */
export const EinvoiceSettingViewSchema = z.object({
  enabled: z.boolean(),
  environment: EinvoiceEnv,
  sellerTaxId: z.string(),
  sellerName: z.string(),
  address: z.string(),
  phone: z.string(),
  fax: z.string(),
  defaultTaxRate: z.number(),
  merchantId: z.string(),
  hasHashKey: z.boolean(),
  hasHashIV: z.boolean(),
  updatedAt: z.string().datetime().nullable(),
});
export type EinvoiceSettingView = z.infer<typeof EinvoiceSettingViewSchema>;

/**
 * 文件抬頭的「我方公司」資料(`CompanyProfile`)自 REQ-20260817004 起改由
 * `company-profile-setting.schema.ts` 定義 —— 那是一份獨立的設定,不再寄生於此。
 * 上面的 `address` / `phone` / `fax` 保留給電子發票設定自己(舊資料的 fallback 來源)。
 */
